import React, { useEffect, useState } from "react";
import { CredentialResponse, GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import {
  GEARSTUDIO_GOOGLE_CLIENT_ID,
  useGearstudioAuth,
} from "@src/providers/GearstudioAuthProvider";

type Mode = "login" | "signup" | "forgot-password" | "reset-password";

type GearstudioAuthPanelProps = {
  formOnly?: boolean;
};

const MIN_PASSWORD_LENGTH = 8;

export const GearstudioAuthPanel = ({ formOnly = false }: GearstudioAuthPanelProps) => {
  const {
    loggedIn,
    user,
    login,
    signup,
    requestPasswordReset,
    resetPassword,
    logout,
    loginWithGoogle,
  } = useGearstudioAuth();

  const [mode, setMode] = useState<Mode>("login");
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupPassword2, setSignupPassword2] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetNewPassword2, setResetNewPassword2] = useState("");
  const [resetUid, setResetUid] = useState("");
  const [resetToken, setResetToken] = useState("");

  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const passwordsMatch = signupPassword === signupPassword2;
  const passwordLongEnough = signupPassword.length >= MIN_PASSWORD_LENGTH;
  const resetPasswordsMatch = resetNewPassword === resetNewPassword2;
  const resetPasswordLongEnough = resetNewPassword.length >= MIN_PASSWORD_LENGTH;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uid = params.get("reset_uid");
    const token = params.get("reset_token");
    if (!uid || !token) {
      return;
    }

    setResetUid(uid);
    setResetToken(token);
    setMode("reset-password");
    setInfoMessage("Set your new password to continue.");
  }, []);

  const clearMessages = () => {
    setErrorMessage("");
    setInfoMessage("");
  };

  const setModeWithCleanup = (nextMode: Mode) => {
    clearMessages();
    setMode(nextMode);
  };

  const clearResetQueryParams = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("reset_uid");
    currentUrl.searchParams.delete("reset_token");
    window.history.replaceState({}, document.title, `${currentUrl.pathname}${currentUrl.search}`);
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loginLoading) {
      return;
    }
    clearMessages();
    setLoginLoading(true);
    try {
      const result = await login(loginEmail.trim(), loginPassword);
      if (!result.ok) {
        setErrorMessage(result.message || "Login failed.");
        return;
      }
      setLoginPassword("");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignupSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (signupLoading || !passwordLongEnough || !passwordsMatch) {
      return;
    }
    clearMessages();
    setSignupLoading(true);
    try {
      const result = await signup({
        email: signupEmail.trim(),
        password: signupPassword,
        password2: signupPassword2,
      });
      if (!result.ok) {
        setErrorMessage(result.message || "Signup failed.");
        return;
      }
      setSignupPassword("");
      setSignupPassword2("");
    } finally {
      setSignupLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (forgotLoading) {
      return;
    }
    clearMessages();
    setForgotLoading(true);
    try {
      const result = await requestPasswordReset(forgotEmail.trim());
      if (!result.ok) {
        setErrorMessage(result.message || "Could not send reset email.");
        return;
      }
      setInfoMessage(
        result.message || "If an account exists for that email, a reset link has been sent.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (resetLoading) {
      return;
    }
    if (!resetUid || !resetToken) {
      setErrorMessage("Invalid password reset link.");
      return;
    }
    if (!resetPasswordLongEnough) {
      setErrorMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (!resetPasswordsMatch) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    clearMessages();
    setResetLoading(true);
    try {
      const result = await resetPassword({
        uid: resetUid,
        token: resetToken,
        password: resetNewPassword,
        password2: resetNewPassword2,
      });
      if (!result.ok) {
        setErrorMessage(result.message || "Could not reset password.");
        return;
      }

      setResetNewPassword("");
      setResetNewPassword2("");
      clearResetQueryParams();
      setInfoMessage(result.message || "Password reset complete. Please log in.");
      setMode("login");
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (googleLoading) {
      return;
    }
    clearMessages();
    if (!response.credential) {
      setErrorMessage("Google login failed to return credentials.");
      return;
    }
    setGoogleLoading(true);
    try {
      const result = await loginWithGoogle(response.credential);
      if (!result.ok) {
        setErrorMessage(result.message || "Google sign-in failed.");
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setErrorMessage("Google Sign-In was unsuccessful. Please try again.");
  };

  if (formOnly && loggedIn) {
    return null;
  }

  if (loggedIn) {
    return (
      <div className="gs-surface-alt w-full p-4 lg:w-[380px]">
        <div className="flex flex-col gap-3">
          <p className="gs-secondary text-sm">
            Signed in as <span className="font-semibold">{user?.email}</span>
          </p>
          <button
            type="button"
            onClick={logout}
            className="gs-btn-primary w-full px-4 py-2 text-sm font-semibold"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GEARSTUDIO_GOOGLE_CLIENT_ID}>
      <div className="gs-surface-alt w-full space-y-4 p-4">
        {mode === "login" || mode === "signup" ? (
          <div className="flex rounded-xl bg-[color:var(--gs-input-bg)] p-1">
            <button
              type="button"
              onClick={() => setModeWithCleanup("login")}
              className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${mode === "login" ? "bg-[color:var(--gs-bg-surface)] text-[var(--gs-text-primary)] shadow-sm" : "text-[var(--gs-text-secondary)] hover:text-[var(--gs-text-primary)]"}`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => setModeWithCleanup("signup")}
              className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${mode === "signup" ? "bg-[color:var(--gs-bg-surface)] text-[var(--gs-text-primary)] shadow-sm" : "text-[var(--gs-text-secondary)] hover:text-[var(--gs-text-primary)]"}`}
            >
              Sign up
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setModeWithCleanup("login")}
            className="text-left text-xs font-semibold text-[var(--gs-text-secondary)] hover:text-[var(--gs-text-primary)]"
          >
            Back to login
          </button>
        )}

        {errorMessage ? (
          <p className="rounded-[var(--gs-radius-input)] border border-[color:rgba(201,107,59,0.45)] bg-[color:rgba(201,107,59,0.12)] px-3 py-2 text-xs text-[var(--gs-text-primary)]">
            {errorMessage}
          </p>
        ) : null}
        {infoMessage ? (
          <p className="rounded-[var(--gs-radius-input)] border border-[color:rgba(16,24,40,0.2)] bg-[color:rgba(16,24,40,0.06)] px-3 py-2 text-xs text-[var(--gs-text-primary)]">
            {infoMessage}
          </p>
        ) : null}

        {mode === "login" || mode === "signup" ? (
          <>
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="signin_with"
                theme="outline"
                size="large"
                shape="rectangular"
                width="340"
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[color:var(--gs-border-subtle)]" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-[color:var(--gs-bg-surface-alt)] px-2 text-xs text-[var(--gs-text-muted)]">
                  or use email
                </span>
              </div>
            </div>
          </>
        ) : null}

        {mode === "login" ? (
          <form className="space-y-3" onSubmit={handleLoginSubmit}>
            <input
              type="email"
              value={loginEmail}
              onChange={(event) => setLoginEmail(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="Email"
              autoComplete="email"
              required
            />
            <input
              type="password"
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="Password"
              autoComplete="current-password"
              required
            />
            <button
              type="submit"
              disabled={loginLoading || googleLoading}
              className="gs-btn-primary w-full px-4 py-2 text-sm font-semibold"
            >
              {loginLoading ? "Signing in..." : "Login"}
            </button>
            <button
              type="button"
              onClick={() => {
                setForgotEmail(loginEmail.trim());
                setModeWithCleanup("forgot-password");
              }}
              className="w-full text-center text-xs font-semibold text-[var(--gs-text-secondary)] hover:text-[var(--gs-text-primary)]"
            >
              Forgot password?
            </button>
          </form>
        ) : null}

        {mode === "signup" ? (
          <form className="space-y-3" onSubmit={handleSignupSubmit}>
            <input
              type="email"
              value={signupEmail}
              onChange={(event) => setSignupEmail(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="Email"
              autoComplete="email"
              required
            />
            <input
              type="password"
              value={signupPassword}
              onChange={(event) => setSignupPassword(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="Password (min 8 chars)"
              autoComplete="new-password"
              required
            />
            <input
              type="password"
              value={signupPassword2}
              onChange={(event) => setSignupPassword2(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="Confirm password"
              autoComplete="new-password"
              required
            />
            {!passwordLongEnough ? (
              <p className="text-xs text-[var(--gs-accent-hover)]">
                Password must be at least {MIN_PASSWORD_LENGTH} characters.
              </p>
            ) : null}
            {!passwordsMatch ? (
              <p className="text-xs text-[var(--gs-accent-hover)]">Passwords do not match.</p>
            ) : null}
            <p className="rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface)] px-3 py-2 text-xs text-[var(--gs-text-secondary)]">
              Note: Gearstudio ordering is currently available only for shipping within the United
              States due to shipping limitations.
            </p>
            <button
              type="submit"
              disabled={signupLoading || googleLoading || !passwordLongEnough || !passwordsMatch}
              className="gs-btn-primary w-full px-4 py-2 text-sm font-semibold"
            >
              {signupLoading ? "Creating account..." : "Create account"}
            </button>
          </form>
        ) : null}

        {mode === "forgot-password" ? (
          <form className="space-y-3" onSubmit={handleForgotPasswordSubmit}>
            <input
              type="email"
              value={forgotEmail}
              onChange={(event) => setForgotEmail(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="Enter your account email"
              autoComplete="email"
              required
            />
            <button
              type="submit"
              disabled={forgotLoading}
              className="gs-btn-primary w-full px-4 py-2 text-sm font-semibold"
            >
              {forgotLoading ? "Sending reset link..." : "Send reset link"}
            </button>
          </form>
        ) : null}

        {mode === "reset-password" ? (
          <form className="space-y-3" onSubmit={handleResetPasswordSubmit}>
            <input
              type="password"
              value={resetNewPassword}
              onChange={(event) => setResetNewPassword(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="New password (min 8 chars)"
              autoComplete="new-password"
              required
            />
            <input
              type="password"
              value={resetNewPassword2}
              onChange={(event) => setResetNewPassword2(event.target.value)}
              className="gs-input px-3 py-2 text-sm"
              placeholder="Confirm new password"
              autoComplete="new-password"
              required
            />
            {!resetPasswordLongEnough ? (
              <p className="text-xs text-[var(--gs-accent-hover)]">
                Password must be at least {MIN_PASSWORD_LENGTH} characters.
              </p>
            ) : null}
            {!resetPasswordsMatch ? (
              <p className="text-xs text-[var(--gs-accent-hover)]">Passwords do not match.</p>
            ) : null}
            <button
              type="submit"
              disabled={resetLoading || !resetPasswordLongEnough || !resetPasswordsMatch}
              className="gs-btn-primary w-full px-4 py-2 text-sm font-semibold"
            >
              {resetLoading ? "Updating password..." : "Set new password"}
            </button>
          </form>
        ) : null}
      </div>
    </GoogleOAuthProvider>
  );
};
